// Point-in-time restore picker (Normal Core MASTER-PRD §6.5.6 / §9, #974) for
// Settings → Workspace → Data. The event log is the source of truth, so any
// moment in the workspace's history is recoverable: pick a restore point
// (signed audit checkpoints anchor the list), preview exactly what rolling
// back would undo, then stage the restore into a DISPOSABLE file. The live
// database is never touched from here — applying the restore is a deliberate
// manual copy-then-swap with the server stopped, and the dialog says so.

import { useState } from "react";
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  IntentBadge,
  SettingsSection,
  formatDateTime,
  formatRelativeTime,
} from "../../index";
import { DataTable } from "../data-table";
import { useApi, apiCall, ApiError } from "../../api/client";
import {
  security,
  type RestorePointsPayload,
  type PrepareRestorePayload,
} from "../../api/contracts/security";

type RestorePointRow = RestorePointsPayload["points"][number];

export function RestorePickerCard() {
  const points = useApi(security.restorePoints, {});
  const [selected, setSelected] = useState<string | null>(null);
  const preview = useApi(
    security.restorePreview,
    selected ? { query: { t: selected } } : null,
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [staged, setStaged] = useState<PrepareRestorePayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const prepare = async () => {
    if (!selected) return;
    setPreparing(true);
    setError(null);
    try {
      const result = await apiCall(security.prepareRestore, {
        body: { t: selected },
      });
      setStaged(result);
      if (!result.ok) setError(result.reason ?? "prepare failed");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPreparing(false);
      setConfirmOpen(false);
    }
  };

  const d = points.data;
  const rows: RestorePointRow[] = d?.points ?? [];

  return (
    <SettingsSection
      title="Point-in-time restore"
      description="Rebuild this workspace as it was at any restore point, staged to a separate file — the live database is never touched."
      id="restore"
    >
      {!d ? (
        <div className="type-body text-muted-foreground">Loading…</div>
      ) : (
        <div className="space-y-3">
          <div className="type-subtext text-muted-foreground">
            {d.log.totalEvents} event{d.log.totalEvents === 1 ? "" : "s"} in
            the log
            {d.log.firstAt && d.log.lastAt ? (
              <>
                {" · "}
                {formatDateTime(d.log.firstAt)} → {formatDateTime(d.log.lastAt)}
              </>
            ) : null}
          </div>

          <DataTable
            rows={rows}
            columns={[
              {
                key: "at",
                header: "Restore point",
                render: (row) => (
                  <span className="font-medium">{formatDateTime(row.at)}</span>
                ),
              },
              {
                key: "age",
                header: "Age",
                render: (row) => (
                  <span className="text-muted-foreground">
                    {formatRelativeTime(row.at)}
                  </span>
                ),
              },
              {
                key: "events",
                header: "Events",
                render: (row) => row.eventCount,
              },
              {
                key: "signed",
                header: "Checkpoint",
                render: (row) =>
                  row.checkpointId == null ? (
                    <Badge variant="outline">unanchored</Badge>
                  ) : row.signed ? (
                    <IntentBadge intent="success" withDot>
                      #{row.checkpointId} signed
                    </IntentBadge>
                  ) : (
                    <IntentBadge intent="warning" withDot>
                      #{row.checkpointId} unsigned
                    </IntentBadge>
                  ),
              },
            ]}
            rowKey={(row) => row.at}
            onRowClick={(row) => setSelected(row.at)}
            rowClassName={(row) =>
              row.at === selected ? "bg-selected" : undefined
            }
            wrapInCard={false}
            empty="No audit checkpoints yet — checkpoints anchor restore points as events accrue."
          />

          {selected && preview.data ? (
            <div className="space-y-2 border-t border-border/40 pt-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="type-body font-medium">
                    Restoring to {formatDateTime(preview.data.upToTime)}
                  </div>
                  <div className="type-subtext text-muted-foreground">
                    {preview.data.eventsAfter} event
                    {preview.data.eventsAfter === 1 ? "" : "s"} after this
                    point would be undone
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() => setConfirmOpen(true)}
                  disabled={preparing}
                >
                  Prepare restore
                </Button>
              </div>
              <div className="space-y-0">
                {preview.data.kinds.map((k) => (
                  <div
                    key={k.kind}
                    className="flex items-center justify-between border-b border-border/40 py-1.5 type-subtext last:border-b-0"
                  >
                    <span>{k.kind}</span>
                    <span
                      className={
                        k.atT === k.now
                          ? "text-muted-foreground"
                          : "font-medium"
                      }
                    >
                      {k.atT === k.now
                        ? `${k.now} (unchanged)`
                        : `${k.now} → ${k.atT}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {staged?.ok ? (
            <Alert>
              <AlertDescription>
                Restore staged — {staged.applied} event
                {staged.applied === 1 ? "" : "s"} replayed into{" "}
                <code className="type-subtext">{staged.stagingPath}</code>. To
                apply: stop the server, replace your live database file with
                the staged one, then relaunch. Your current database is
                untouched until you do.
              </AlertDescription>
            </Alert>
          ) : null}
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stage a point-in-time restore?</DialogTitle>
            <DialogDescription>
              This replays the event log up to{" "}
              {selected ? formatDateTime(selected) : "the selected point"} into
              a separate staging file. Nothing changes until you manually swap
              it in: stop the server, replace the live database with the
              staged file, relaunch. Swapping discards everything after the
              restore point.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={prepare} disabled={preparing}>
              {preparing ? "Staging…" : "Stage restore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SettingsSection>
  );
}
